import type { Ticket, TicketFilter } from '@/types/tickets';
import { isAssignedToMe } from '@/lib/ticket-utils';

/**
 * True se o filtro tem algum critério ativo (prioridade específica ou "atribuído para mim").
 * 'all' é tratado como inativo: equivale a "sem filtro de prioridade" (matchesFilter ignora 'all'),
 * então não deve destacar o botão nem exibir "Limpar filtros".
 */
export function isFilterActive(filter: TicketFilter): boolean {
  return (filter.priority !== null && filter.priority !== 'all') || filter.assignedToMe;
}

/** True se o ticket bate com o termo de busca (id, título, descrição, cliente, motivo). */
export function matchesSearch(ticket: Ticket, searchTerm: string): boolean {
  if (!searchTerm) return true;
  const term = searchTerm.toLowerCase().trim();

  // Busca por número do ticket: aceita "#123", "123" ou "id 123".
  // Quando o termo é puramente numérico (com # opcional), busca SÓ pelo id.
  const idTerm = term.replace(/^#/, '');
  if (/^\d+$/.test(idTerm)) {
    return String(ticket.id).includes(idTerm);
  }

  return Boolean(
    String(ticket.id) === idTerm ||
    ticket.title?.toLowerCase().includes(term) ||
    ticket.description?.toLowerCase().includes(term) ||
    ticket.client?.legal_name?.toLowerCase().includes(term) ||
    ticket.client?.trade_name?.toLowerCase().includes(term) ||
    ticket.reason?.name?.toLowerCase().includes(term)
  );
}

/** True se o ticket passa por UM filtro (prioridade + atribuído para mim). */
export function matchesFilter(
  ticket: Ticket,
  filter: TicketFilter,
  currentUserId: number | null | undefined
): boolean {
  if (filter.priority && filter.priority !== 'all') {
    if (ticket.priority !== filter.priority) return false;
  }
  if (filter.assignedToMe) {
    if (!isAssignedToMe(ticket, currentUserId ?? null)) return false;
  }
  return true;
}

export interface MatchTicketParams {
  searchTerm: string;
  globalFilter: TicketFilter;
  columnFilter: TicketFilter;
  currentUserId: number | null | undefined;
}

/**
 * Combina (E) busca + filtro global + filtro da coluna.
 * Um ticket só aparece se passar nos três.
 */
export function matchesTicket(ticket: Ticket, params: MatchTicketParams): boolean {
  const { searchTerm, globalFilter, columnFilter, currentUserId } = params;
  if (!matchesSearch(ticket, searchTerm)) return false;
  if (!matchesFilter(ticket, globalFilter, currentUserId)) return false;
  if (!matchesFilter(ticket, columnFilter, currentUserId)) return false;
  return true;
}
